import { useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";

/**
 * RefineNoteOnCard — the "Refine goal" affordance for the projects board.
 * Opens a one-line input inline; the note rides the refineNote verb and
 * lands in the item's payload.jeff.refine_notes list (newest first on render).
 * Props:
 *   board    the parent's useBoard() hook object
 *   slug     the row slug to target
 *   testId   passthrough
 */
export const RefineNoteOnCard = ({ board, slug, testId = "refine-note" }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    const res = await board.act("refineNote", slug, t);
    setBusy(false);
    if (res) {
      setText("");
      setOpen(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } else {
      setError("That didn't go through — try again.");
    }
  };

  // No affordance in baked/offline mode.
  if (!board?.live) return null;

  const notes = board.items?.find((r) => r.slug === slug)?.payload?.jeff?.refine_notes || [];

  return (
    <div className="mt-3">
      {!open && (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            data-testid={testId}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            className="rounded-control px-2 py-1 font-mono text-[11px] text-os-ink-3 hover:bg-os-wash hover:text-os-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink"
            aria-label="Refine goal"
          >
            Refine goal
          </button>
          {saved && <MonoMeta data-testid={`${testId}-saved`}>Saved</MonoMeta>}
        </span>
      )}
      {open && (
        <form
          data-testid={`${testId}-form`}
          onClick={(e) => e.stopPropagation()}
          onSubmit={send}
          className="flex items-center gap-2"
        >
          <input
            data-testid={`${testId}-input`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a goal note for the record…"
            autoFocus
            className="min-w-[12rem] flex-1 rounded-control border border-os-border bg-white px-2.5 py-1.5 text-[13px] outline-none placeholder:text-os-ink-4 focus:border-os-border-strong"
          />
          <button
            type="submit"
            data-testid={`${testId}-send`}
            disabled={busy || !text.trim()}
            className="rounded-control border border-os-border px-2.5 py-1.5 font-mono text-[12px] text-os-ink hover:border-os-border-strong disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-control px-2 py-1.5 font-mono text-[12px] text-os-ink-3 hover:text-os-ink"
          >
            Cancel
          </button>
          {error && (
            <span data-testid={`${testId}-error`} className="text-[12px] text-os-red">{error}</span>
          )}
        </form>
      )}
      {notes.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {[...notes].reverse().map((n, i) => (
            <li
              key={i}
              data-testid={`${testId}-item-${notes.length - 1 - i}`}
              className="text-[0.8125rem] leading-[1.4] text-os-ink-2"
            >
              <span className="font-medium text-os-ink">{n.text}</span>
              <span className="ml-1.5 font-mono text-[0.6875rem] text-os-ink-4">{n.at?.slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default RefineNoteOnCard;
